// =============================================================
// injected.ts — window.ethereum(MetaMask 등) 기반 WalletAdapter
//  Reown AppKit 으로 교체하려면 이 파일 대신 reown.ts 를 만들어
//  동일 WalletAdapter 인터페이스를 구현하고 sdk/index.ts 에서 주입만 바꾼다.
// =============================================================

import { BrowserProvider } from 'ethers';
import { CHAIN } from './config';
import { SdkError, type WalletAdapter, type WalletSnapshot } from './types';

type Eip1193 = {
  request(args: { method: string; params?: unknown[] }): Promise<unknown>;
  on?(event: string, handler: (...args: unknown[]) => void): void;
  removeListener?(event: string, handler: (...args: unknown[]) => void): void;
};

function getInjectedEthereum(): Eip1193 | undefined {
  return (window as unknown as { ethereum?: Eip1193 }).ethereum;
}

/**
 * 모바일 MetaMask 내장 브라우저 stuck 대응.
 *  · eth_requestAccounts 시트가 렌더링만 되고 응답 채널이 끊기는 경우가 있다.
 *    (시트는 보이는데 승인 눌러도 dApp 으로 결과가 안 옴)
 *  · 이때도 사용자가 실제로 승인하면 계정 상태 자체는 바뀌므로,
 *    eth_accounts 를 폴링해 '죽은 시트' 뒤의 실제 승인을 감지한다.
 *  · requestAccounts 응답 / 폴링 감지 / 타임아웃 중 먼저 도달하는 것을 취한다.
 */
async function requestAccountsResilient(
  eth: Eip1193,
  opts: { timeoutMs: number; pollMs: number; onStuck?: () => void; stuckAfterMs: number },
): Promise<string[]> {
  const { timeoutMs, pollMs, onStuck, stuckAfterMs } = opts;
  const start = Date.now();
  let settled = false;
  let stuckFired = false;

  return new Promise<string[]>((resolve, reject) => {
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearInterval(poll);
      clearTimeout(timer);
      fn();
    };

    // 1) 표준 연결 요청
    (eth.request({ method: 'eth_requestAccounts' }) as Promise<string[]>).then(
      (accs) => finish(() => resolve(accs)),
      (err) => finish(() => reject(err)),
    );

    // 2) eth_accounts 폴링 — 시트가 죽어도 실제 승인이면 여기서 잡힌다
    const poll = setInterval(() => {
      // 오래 걸리면 stuck 안내 콜백(1회)
      if (!stuckFired && onStuck && Date.now() - start > stuckAfterMs) {
        stuckFired = true;
        onStuck();
      }
      void (async () => {
        try {
          const accs = (await eth.request({ method: 'eth_accounts' })) as string[];
          if (accs && accs.length > 0) finish(() => resolve(accs));
        } catch {
          /* 폴링 실패는 무시 */
        }
      })();
    }, pollMs);

    // 3) 타임아웃 — 명시적 실패
    const timer = setTimeout(() => {
      finish(() =>
        reject(new SdkError('USER_REJECTED', '지갑이 응답하지 않습니다. 페이지를 새로고침하거나 MetaMask 앱을 재시작해 주세요.')),
      );
    }, timeoutMs);
  });
}

export class InjectedWalletAdapter implements WalletAdapter {
  private provider: BrowserProvider | null = null;
  private address: string | null = null;
  private chainOk = false;
  private listeners = new Set<(s: WalletSnapshot) => void>();
  // 진행 중인 연결 요청(single-flight).
  //  · MetaMask 는 이전 eth_requestAccounts 가 처리 중일 때 새 요청을 무시/거부한다.
  //  · 중복 클릭 시 새 요청을 만들지 않고 같은 promise 를 재사용해 충돌을 막는다.
  private connectInFlight: Promise<string> | null = null;
  // 지갑이 응답하지 않을 때(stuck) UI 에 알리는 리스너.
  private stuckListeners = new Set<() => void>();

  /** 지갑 stuck 알림 구독 (UI 가 '새로고침' 안내를 띄우기 위함). */
  onStuck(cb: () => void): () => void {
    this.stuckListeners.add(cb);
    return () => this.stuckListeners.delete(cb);
  }
  private emitStuck(): void {
    for (const cb of this.stuckListeners) cb();
  }

  private get injected(): Eip1193 {
    const eth = getInjectedEthereum();
    if (!eth) throw new SdkError('NO_WALLET', '지갑이 감지되지 않습니다.');
    return eth;
  }

  async connect(): Promise<string> {
    // 이미 연결됨 → 즉시 반환
    if (this.address) return this.address;
    // 진행 중인 요청이 있으면 그 promise 를 재사용(중복 팝업/충돌 방지)
    if (this.connectInFlight) return this.connectInFlight;

    this.connectInFlight = this.doConnect().finally(() => {
      // 성공/실패와 무관하게 in-flight 해제 → 다음 클릭이 새로 시도 가능
      this.connectInFlight = null;
    });
    return this.connectInFlight;
  }

  private async doConnect(): Promise<string> {
    const eth = this.injected;
    this.provider = new BrowserProvider(eth as never, 'any');

    // 1) 팝업 없이 이미 승인된 계정이 있는지 먼저 확인
    //    (앞선 시도에서 사용자가 실제로는 승인했는데 시트만 닫힌 경우 등)
    let accounts: string[] = [];
    try {
      accounts = (await eth.request({ method: 'eth_accounts' })) as string[];
    } catch {
      accounts = [];
    }

    // 2) 승인된 계정이 없으면 요청. 폴링+타임아웃으로 stuck 대응.
    if (!accounts || accounts.length === 0) {
      accounts = await requestAccountsResilient(eth, {
        timeoutMs: 30_000,   // 30초 후 명시적 실패
        pollMs: 1_500,       // 1.5초마다 eth_accounts 확인
        stuckAfterMs: 8_000, // 8초 넘게 응답 없으면 stuck 안내
        onStuck: () => this.emitStuck(),
      });
    }

    if (!accounts || accounts.length === 0) {
      throw new SdkError('USER_REJECTED', '연결할 계정이 없습니다.');
    }

    this.address = accounts[0].toLowerCase();
    await this.ensureChain();

    // 계정/체인 변경 → 스냅샷 반영
    eth.on?.('accountsChanged', (...a: unknown[]) => {
      const accs = a[0] as string[];
      this.address = accs?.[0]?.toLowerCase() ?? null;
      this.emit();
    });
    eth.on?.('chainChanged', () => {
      void this.refreshChain();
    });

    this.emit();
    return this.address!;
  }

  /** 이전에 승인된 계정이 있으면 조용히 복원 (eth_accounts 는 팝업 없음). */
  async restore(): Promise<void> {
    const eth = getInjectedEthereum();
    if (!eth) return;
    try {
      const accs = (await eth.request({ method: 'eth_accounts' })) as string[];
      if (!accs?.length) return;
      this.provider = new BrowserProvider(eth as never, 'any');
      this.address = accs[0].toLowerCase();
      await this.refreshChain();
      eth.on?.('accountsChanged', (...a: unknown[]) => {
        const next = a[0] as string[];
        this.address = next?.[0]?.toLowerCase() ?? null;
        this.emit();
      });
      eth.on?.('chainChanged', () => {
        void this.refreshChain();
      });
      this.emit();
    } catch {
      /* 복원 실패 → 비연결 상태 유지 */
    }
  }

  async disconnect(): Promise<void> {
    this.provider = null;
    this.address = null;
    this.chainOk = false;
    this.emit();
  }

  async getProvider(): Promise<BrowserProvider> {
    if (!this.provider) throw new SdkError('NO_WALLET', '지갑이 연결되지 않았습니다.');
    return this.provider;
  }

  getAddress(): string | null {
    return this.address;
  }

  async ensureChain(): Promise<void> {
    const provider = await this.getProvider();
    const net = await provider.getNetwork();
    if (Number(net.chainId) === CHAIN.id) {
      this.chainOk = true;
      return;
    }
    try {
      await provider.send('wallet_switchEthereumChain', [{ chainId: CHAIN.idHex }]);
    } catch (err) {
      const e = err as { code?: number; message?: string };
      // 4902 = 지갑에 체인 없음 → 추가
      if (e.code === 4902 || /Unrecognized chain/i.test(e.message ?? '')) {
        await provider.send('wallet_addEthereumChain', [
          {
            chainId: CHAIN.idHex,
            chainName: CHAIN.name,
            nativeCurrency: CHAIN.nativeCurrency,
            rpcUrls: [CHAIN.rpc],
            blockExplorerUrls: [CHAIN.explorer],
          },
        ]);
      } else {
        throw err;
      }
    }
    this.chainOk = true;
  }

  private async refreshChain(): Promise<void> {
    try {
      const provider = await this.getProvider();
      const net = await provider.getNetwork();
      this.chainOk = Number(net.chainId) === CHAIN.id;
    } catch {
      this.chainOk = false;
    }
    this.emit();
  }

  onChange(cb: (s: WalletSnapshot) => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  snapshot(): WalletSnapshot {
    return {
      connected: !!this.address,
      address: this.address,
      short: this.address ? `${this.address.slice(0, 6)}…${this.address.slice(-4)}` : null,
      chainOk: this.chainOk,
    };
  }

  private emit(): void {
    const snap = this.snapshot();
    for (const cb of this.listeners) cb(snap);
  }
}